import { useState, type FormEvent } from 'react';

import PasswordInput from '../Common/PasswordInput';
import useAuth from '../../hooks/useAuth';
import { ApiClientError } from '../../services/api';

type RegisterProps = {
  setupRequired: boolean;
  onBack: () => void;
  onRegistered: () => void;
};

const Register = ({ setupRequired, onBack, onRegistered }: RegisterProps) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();

  const handleRegister = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('De wachtwoorden komen niet overeen.');
      return;
    }
    setSubmitting(true);
    try {
      await register(username, email, password);
      onRegistered();
    } catch (reason) {
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : 'Registreren is momenteel niet mogelijk.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="auth-card" aria-labelledby="register-title">
      <div className="brand-mark" aria-hidden="true">
        AI
      </div>
      <h1 id="register-title">
        {setupRequired ? 'Eerste beheerder instellen' : 'Account aanmaken'}
      </h1>
      <p className="muted">
        Gebruik minimaal 12 tekens voor een sterk wachtwoord.
      </p>
      <form onSubmit={handleRegister} className="form-stack">
        <label>
          Naam
          <input
            type="text"
            name="username"
            autoComplete="name"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            minLength={2}
            maxLength={50}
            disabled={submitting}
            required
          />
        </label>
        <label>
          E-mailadres
          <input
            type="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            required
          />
        </label>
        <PasswordInput
          label="Wachtwoord"
          name="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={12}
          maxLength={128}
          disabled={submitting}
          required
        />
        <PasswordInput
          label="Wachtwoord herhalen"
          name="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          minLength={12}
          maxLength={128}
          disabled={submitting}
          required
        />
        {error && (
          <p className="notice error" role="alert">
            {error}
          </p>
        )}
        <button className="button primary" type="submit" disabled={submitting}>
          {submitting ? 'Account wordt aangemaakt…' : 'Account aanmaken'}
        </button>
      </form>
      {!setupRequired && (
        <button type="button" className="button link" onClick={onBack}>
          Terug naar inloggen
        </button>
      )}
    </section>
  );
};

export default Register;